"use client";

import { motion } from "framer-motion";
import { Hexagon, Layers, Code, ShieldCheck, Rocket } from "lucide-react";

export default function DetailedProcess() {
    const steps = [
        {
            icon: <Hexagon className="w-6 h-6 text-sky-400" />,
            title: "Strategy & Discovery",
            desc: "We analyze market gaps and user pain points to define a clear roadmap. Using competitive analysis, we pinpoint opportunities for innovation available in the ecosystem.",
            tags: ["Market Research", "User Personas", "Roadmapping", "Tech Stack Selection"]
        },
        {
            icon: <Layers className="w-6 h-6 text-sky-400" />,
            title: "Experience Design",
            desc: "Transforming concepts into tangible blueprints. We craft intuitive wireframes and stunning high-fidelity interfaces that prioritize user engagement and accessibility.",
            tags: ["Wireframing", "Prototyping", "Design System", "Interaction Design", "Motion"]
        },
        {
            icon: <Code className="w-6 h-6 text-sky-400" />,
            title: "Engineering",
            desc: "Building the core. We implement scalable architectures using modern frameworks, ensuring security, performance, and seamless API integration across all endpoints.",
            tags: ["Frontend", "Backend API", "Database Design", "Security", "Optimization"]
        },
        {
            icon: <ShieldCheck className="w-6 h-6 text-sky-400" />,
            title: "Quality Assurance",
            desc: "Rigorous testing across devices and scenarios. We squash bugs and optimize performance to guarantee a crash-free experience before the world sees it.",
            tags: ["Unit Testing", "E2E Testing", "Performance", "Security Audit", "Device Lab"]
        },
        {
            icon: <Rocket className="w-6 h-6 text-sky-400" />,
            title: "Launch & Evolution",
            desc: "The beginning of the journey. We handle store submissions and monitor analytics to iterate and scale based on real user feedback.",
            tags: ["Store Deployment", "Analytics", "CI/CD", "Post-Launch Support", "Scaling"]
        }
    ];

    return (
        <section className="bg-[#050505] text-white pt-10 pb-4 border-t border-white/5">
            <div className="container mx-auto px-6 md:px-20 lg:px-40">

                {/* Header Row */}
                <div className="flex justify-between items-start mb-20 text-sm md:text-base text-gray-500 font-medium">
                    <div>
                        <span className="font-bold tracking-tight text-white/40 text-lg uppercase">The Process</span>
                    </div>
                    <div className="text-right text-gray-500 text-sm font-medium">
                        <span className="block mb-1 text-white/40">From Concept</span>
                        <span className="text-white/80">to deployable Product</span>
                    </div>
                </div>

                {/* Steps List */}
                <div className="flex flex-col">
                    {steps.map((step, index) => (
                        <div
                            key={index}
                            className="group flex flex-col lg:flex-row gap-5 lg:gap-20 py-8 lg:py-16 border-t border-white/10 first:border-0"
                        >
                            {/* Icon & Title Row (Mobile) / Icon Column (Desktop) */}
                            <div className="flex items-center gap-4 lg:block lg:shrink-0">
                                <div className="w-14 h-14 lg:w-16 lg:h-16 shrink-0 rounded-full bg-[#111] border border-white/5 flex items-center justify-center group-hover:border-sky-500/30 transition-colors duration-300">
                                    {step.icon}
                                </div>
                                <h3 className="text-2xl sm:text-3xl font-bold tracking-tighter lg:hidden">{step.title}</h3>
                            </div>

                            {/* Content Column */}
                            <div className="lg:w-1/2">
                                <h3 className="hidden lg:block text-4xl lg:text-5xl font-bold tracking-tighter mb-6">{step.title}</h3>
                                <p className="text-gray-400 text-lg md:text-xl font-light leading-relaxed max-w-xl">
                                    {step.desc}
                                </p>
                            </div>

                            {/* Tags Column */}
                            <div className="lg:w-1/2 flex items-start lg:justify-end">
                                <div className="flex flex-wrap gap-2 sm:gap-3 lg:justify-end max-w-md">
                                    {step.tags.map((tag, i) => (
                                        <span
                                            key={i}
                                            className="px-3 sm:px-5 py-1.5 sm:py-2.5 rounded-full bg-[#1a1a1a] text-xs sm:text-sm text-gray-300 border border-white/5 hover:bg-[#222] hover:border-white/10 transition-colors cursor-default"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>



            </div>
        </section>
    );
}
